import { useState, useEffect, useMemo, useRef, type ReactNode, type CSSProperties } from 'react';
import { useAutosave, type AutosaveStatus } from '../../hooks/useAutosave';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { LoadingBlock, Spinner, BusyLabel } from '../Spinner';
import { api } from '../../api/client';
import type { Account, InterruptStatus } from '../../api/client';
import { FeatherIcon } from '../FeatherIcon';
import { formatInterruptText, interruptNameToKey } from '../../utils/formatInterruptText';
import {
  groupInterrupts,
  interruptsEnabledSummary,
  stepDetectionSeconds,
  detectionMinutesLabel,
  stepTurnThreshold,
  isLongChatFlagOn,
  stepCleanupDays,
  moneyInOnly,
  canApplyMoneyInOnly,
  telegramAutosaveTarget,
  type ActivityOptOuts,
} from './notificationsLayout';

/** Compact-layout palette. Flat constants rather than CSS vars: the tab paints a
 *  self-contained surface and every value below comes straight from the design. */
const C = {
  bg: 'var(--om-surface)',
  card: 'var(--om-card)',
  frameLine: 'var(--om-border-lo)',
  cardLine: 'var(--om-border)',
  rowLine: 'var(--om-divider)',
  subRow: 'var(--om-panel)',
  input: 'var(--om-panel)',
  text: 'var(--om-text)',
  muted: 'var(--om-sub)',
  dim: 'var(--om-muted)',
  accent: 'var(--om-accent)',
  accentLite: 'var(--om-accent-2)',
  accentSoft: 'var(--om-accent-3)',
  accentTint: 'color-mix(in srgb, var(--om-accent) 10%, transparent)',
  accentRing: '0 0 0 3px color-mix(in srgb, var(--om-accent) 18%, transparent)',
  warn: '#f0955a',
  ok: '#3ddc84',
  err: '#f87171',
  pop: 'var(--om-hover)',
  popLine: 'var(--om-border-strong)',
};
const FONT = 'var(--font-sans)';

/** How long after the last keystroke the Telegram chat ID is persisted. */
const TELEGRAM_AUTOSAVE_MS = 700;

/** Supported client-facing notification languages (mirrors bigdayz.new). Option
 *  labels are endonyms (shown in their own script) and are not themselves translated. */
type NotifLang = 'en' | 'ru' | 'uk';
const LANG_OPTIONS: { id: NotifLang; label: string }[] = [
  { id: 'en', label: 'English' },
  { id: 'ru', label: 'Русский' },
  { id: 'uk', label: 'Українська' },
];
function normalizeLang(value: string | null | undefined): NotifLang {
  return value === 'ru' || value === 'uk' ? value : 'en';
}

/** Notification format (ADR 0030), mirrors bigdayz.new. Fail-soft to verbose. */
type NotifFormat = 'verbose' | 'compact';
function normalizeFormat(value: string | null | undefined): NotifFormat {
  return value === 'compact' ? 'compact' : 'verbose';
}

function readOptOuts(a: Account): ActivityOptOuts {
  return {
    ppvSent: a.notify_ppv_sent ?? true,
    freeContentSent: a.notify_free_content_sent ?? true,
    tipAsk: a.notify_tip_ask ?? true,
    vipOfferSent: a.notify_vip_offer_sent ?? true,
    chatEnded: a.notify_chat_ended ?? true,
    allPaymentEvents: a.notify_all_payment_events ?? false,
  };
}

/** Chat-tracking list mode (ADR 0016), mirrors bigdayz.new. */
type TrackingMode = 'off' | 'daily' | 'custom';
function normalizeMode(value: string | null | undefined): TrackingMode {
  return value === 'off' || value === 'custom' ? value : 'daily';
}
const DD_MM_RE = /^\d{2}\.\d{2}$/;
/** Validate the custom bucket name (mirrors the backend guard). Returns an error
 *  message key-ready string, or '' when valid. reserved = interrupt list + ignored. */
function validateCustomName(name: string, reserved: string[]): string {
  const trimmed = name.trim();
  if (!trimmed) return 'empty';
  if (DD_MM_RE.test(trimmed)) return 'ddmm';
  const lower = trimmed.toLowerCase();
  if (reserved.some((r) => (r || '').trim().toLowerCase() === lower)) return 'reserved';
  return '';
}

interface NotificationsSubTabProps {
  account: Account;
  onAccountUpdate?: (account: Account) => void;
}

/** DB / API name is usually fan_stopped_responding; tolerate spaced Title Case. */
function isFanStoppedRespondingInterrupt(n: InterruptStatus): boolean {
  return n.name.trim().toLowerCase().replace(/\s+/g, '_') === 'fan_stopped_responding';
}

/** Fan-sent-content handling mode (replaces the binary fan_sent_media_file interrupt).
 *  A missing policy row fails soft to 'handoff'. */
type FanSentMode = 'ignore' | 'handoff' | 'analyse';
function normalizeFanSentMode(value: string | null | undefined): FanSentMode {
  return value === 'ignore' || value === 'analyse' ? value : 'handoff';
}

type TelegramSaveState = 'idle' | 'saving' | 'saved' | 'error';

export function NotificationsSubTab({ account, onAccountUpdate }: NotificationsSubTabProps) {
  const { t } = useTranslation('chatAssistant');
  const [telegramChatId, setTelegramChatId] = useState(account.telegram_chat_id || '');
  const [telegramSaveState, setTelegramSaveState] = useState<TelegramSaveState>('idle');
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [testErrorMsg, setTestErrorMsg] = useState('');
  // True when the last test discovered the group had been upgraded to a supergroup
  // and we auto-reconnected it (chat id swapped for the new one).
  const [testMigrated, setTestMigrated] = useState(false);
  const [notifications, setNotifications] = useState<InterruptStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [ignoreDetectionTime, setIgnoreDetectionTime] = useState(account.ignore_detection_time ?? 1800);
  const [interruptListName, setInterruptListName] = useState(account.interrupt_list_name ?? 'Notifications');
  const [notificationLanguage, setNotificationLanguage] = useState<NotifLang>(normalizeLang(account.notification_language));
  // Chat-tracking list (ADR 0016)
  const [trackingMode, setTrackingMode] = useState<TrackingMode>(normalizeMode(account.chat_tracking_mode));
  const [trackingCustomName, setTrackingCustomName] = useState(account.chat_tracking_custom_name ?? '');
  const [trackingCleanupEnabled, setTrackingCleanupEnabled] = useState(account.chat_tracking_cleanup_enabled ?? true);
  const [trackingCleanupDays, setTrackingCleanupDays] = useState(account.chat_tracking_cleanup_after_days ?? 2);
  // Turn-threshold flag (ADR 0054): threshold (blank = off), notify toggle, target list.
  const [turnThreshold, setTurnThreshold] = useState<number | ''>(account.turn_threshold ?? '');
  const [turnThresholdNotify, setTurnThresholdNotify] = useState<boolean>(account.turn_threshold_notify ?? false);
  const [turnThresholdListName, setTurnThresholdListName] = useState<string>(account.turn_threshold_list_name ?? '');
  // Notification format + activity opt-outs (ADR 0030)
  const [notifFormat, setNotifFormat] = useState<NotifFormat>(normalizeFormat(account.notification_format));
  const [optOuts, setOptOuts] = useState<ActivityOptOuts>(readOptOuts(account));
  // Fan-sent-content mode (3-way: ignore / handoff / analyse). Loaded from its own
  // policy endpoint; a missing row reads as 'handoff'. Replaces the old binary
  // fan_sent_media_file interrupt toggle.
  const [fanSentMode, setFanSentMode] = useState<FanSentMode>('handoff');
  // Spam detection (ADR 0039/0045) — a single combined switch that drives both the LLM
  // spam judgement and the verified-creator pre-filter, plus the folder spam fans go into.
  // Loaded from its own policy endpoint; a missing row reads as ON / 'ignored_spam'.
  const [spamEnabled, setSpamEnabled] = useState(true);
  const [spamFolder, setSpamFolder] = useState('ignored_spam');
  const [spamLoaded, setSpamLoaded] = useState(false);
  // Status of the one-shot saves (interrupt toggles, fan-sent mode), shown in the
  // same chip as the debounced autosaves.
  const [quick, setQuick] = useState<{ status: AutosaveStatus; error: string | null }>({ status: 'idle', error: null });

  // The stored Telegram chat ID (what the autosave compares against).
  const telegramSnapshot = useRef(account.telegram_chat_id || '');
  // Names a custom bucket may not collide with (would silence chatted fans).
  const reservedListNames = useMemo(
    () => [account.interrupt_list_name ?? 'Notifications', ...(account.lists_to_ignore ?? [])],
    [account.interrupt_list_name, account.lists_to_ignore],
  );

  useEffect(() => {
    setTelegramChatId(account.telegram_chat_id || '');
    setIgnoreDetectionTime(account.ignore_detection_time ?? 1800);
    setInterruptListName(account.interrupt_list_name ?? 'Notifications');
    setNotificationLanguage(normalizeLang(account.notification_language));
    setTrackingMode(normalizeMode(account.chat_tracking_mode));
    setTrackingCustomName(account.chat_tracking_custom_name ?? '');
    setTrackingCleanupEnabled(account.chat_tracking_cleanup_enabled ?? true);
    setTrackingCleanupDays(account.chat_tracking_cleanup_after_days ?? 2);
    setTurnThreshold(account.turn_threshold ?? '');
    setTurnThresholdNotify(account.turn_threshold_notify ?? false);
    setTurnThresholdListName(account.turn_threshold_list_name ?? '');
    setNotifFormat(normalizeFormat(account.notification_format));
    setOptOuts(readOptOuts(account));
    setTelegramSaveState('idle');

    telegramSnapshot.current = account.telegram_chat_id || '';
    fetchNotifications();
    fetchFanSentContentPolicy();
    fetchSpamPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.account_id]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await api.getAccountInterrupts(account.account_id);
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetched separately so a failure here never blocks the interrupts load, and
  // fails soft to 'handoff' (matching the backend's missing-row default).
  const fetchFanSentContentPolicy = async () => {
    try {
      const pol = await api.getFanSentContentPolicy(account.account_id);
      const mode = normalizeFanSentMode(pol.fan_sent_content_mode);
      setFanSentMode(mode);
    } catch {
      setFanSentMode('handoff');
    }
  };

  // Fetched separately so a failure here never blocks the interrupts load, and fails soft
  // to ON / 'ignored_spam' (matching the backend's missing-row default).
  const fetchSpamPolicy = async () => {
    try {
      const pol = await api.getSpamPolicy(account.account_id);
      const enabled = pol.enabled ?? true;
      const folder = (pol.spam_list_name || 'ignored_spam').trim() || 'ignored_spam';
      setSpamEnabled(enabled);
      setSpamFolder(folder);
      setSpamLoaded(true);
    } catch {
      // Unknown stored state: show the defaults but never autosave over it.
      setSpamEnabled(true);
      setSpamFolder('ignored_spam');
    }
  };

  // ── Telegram chat ID autosaves as you type ──────────────────────────
  // Everything else on the tab still commits through the Save pill; this one
  // field is persisted on its own so an operator who pastes an ID and walks
  // away (or jumps straight to Test) doesn't silently lose it.
  useEffect(() => {
    const { shouldSave, value } = telegramAutosaveTarget(telegramChatId, telegramSnapshot.current);
    if (!shouldSave) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setTelegramSaveState('saving');
      try {
        const updated = await api.updateAccount(account.account_id, { telegram_chat_id: value });
        if (cancelled) return;
        telegramSnapshot.current = updated.telegram_chat_id || '';
        onAccountUpdate?.(updated);
        setTelegramSaveState('saved');
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to save Telegram chat ID', err);
        setTelegramSaveState('error');
      }
    }, TELEGRAM_AUTOSAVE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telegramChatId, account.account_id]);

  useEffect(() => {
    if (telegramSaveState !== 'saved') return;
    const timer = window.setTimeout(() => setTelegramSaveState('idle'), 2500);
    return () => window.clearTimeout(timer);
  }, [telegramSaveState]);

  const handleTestTelegram = async () => {
    setTestingTelegram(true);
    setTestStatus('idle');
    setTestErrorMsg('');
    setTestMigrated(false);
    try {
      // The test endpoint persists whatever ID we pass and sends to it directly,
      // so testing a freshly-typed ID works without a separate Save (and without
      // racing a lagging read-replica). It also self-heals: if the group was
      // upgraded to a supergroup, it follows the new id and returns it here.
      const trimmedId = telegramChatId.trim();
      const res = await api.testTelegramNotification(account.account_id, {
        telegramChatId: trimmedId,
        notificationLanguage,
      });
      // Prefer the id the server actually delivered to (updated on migration).
      const deliveredId = res.telegram_chat_id ?? trimmedId;
      setTestMigrated(!!res.migrated);
      if (deliveredId !== telegramSnapshot.current) {
        telegramSnapshot.current = deliveredId;
        setTelegramChatId(deliveredId);
      }
      onAccountUpdate?.({
        ...account,
        telegram_chat_id: deliveredId || null,
        telegram_health: res.telegram_health ?? 'ok',
        telegram_health_detail: res.telegram_health_detail ?? null,
        telegram_health_updated_at: new Date().toISOString(),
      });
      setTestStatus('sent');
    } catch (err: unknown) {
      setTestErrorMsg(err instanceof Error ? err.message : t('notifications.failedSendTest'));
      setTestStatus('error');
    } finally {
      setTestingTelegram(false);
    }
  };

  // ── Everything saves on change ─────────────────────────────────────
  // Toggles and the fan-sent mode save the moment they flip (optimistic, reverted on
  // failure); the account fields and the spam folder autosave after a short pause.
  const quickSave = async (run: () => Promise<void>, revert: () => void) => {
    setQuick({ status: 'saving', error: null });
    try {
      await run();
      setQuick({ status: 'saved', error: null });
    } catch (err) {
      revert();
      setQuick({ status: 'error', error: err instanceof Error ? err.message : t('notifications.failedSave') });
    }
  };
  useEffect(() => {
    if (quick.status !== 'saved') return;
    const h = window.setTimeout(() => setQuick({ status: 'idle', error: null }), 2500);
    return () => window.clearTimeout(h);
  }, [quick.status]);

  const toggleNotification = (id: number) => {
    const cur = notifications.find((n) => n.id === id);
    if (!cur) return;
    const next = !cur.status;
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, status: next } : n)));
    void quickSave(
      async () => {
        const u = await api.updateInterruptStatus(account.account_id, id, next);
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, ...u } : n)));
      },
      () => setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, status: cur.status } : n))),
    );
  };

  const changeFanSentMode = (m: FanSentMode) => {
    const prev = fanSentMode;
    setFanSentMode(m);
    void quickSave(
      async () => { await api.putFanSentContentPolicy(account.account_id, { fan_sent_content_mode: m }); },
      () => setFanSentMode(prev),
    );
  };

  // Inline validation — an invalid value is held back (not saved) until fixed.
  const interruptListNameError = interruptListName.trim() ? '' : t('notifications.listNameEmpty');
  const trackingCustomNameError = trackingMode === 'custom' ? validateCustomName(trackingCustomName, reservedListNames) : '';

  const accountDraft = useMemo(() => ({
    interrupt_list_name: interruptListName.trim(),
    ignore_detection_time: ignoreDetectionTime,
    notification_language: notificationLanguage,
    chat_tracking_mode: trackingMode,
    // Only send a name in custom mode; otherwise keep whatever is stored.
    chat_tracking_custom_name: trackingMode === 'custom' ? trackingCustomName.trim() : (account.chat_tracking_custom_name ?? null),
    chat_tracking_cleanup_enabled: trackingCleanupEnabled,
    chat_tracking_cleanup_after_days: trackingCleanupDays,
    turn_threshold: turnThreshold === '' || turnThreshold === 0 ? null : Number(turnThreshold),
    turn_threshold_notify: turnThresholdNotify,
    turn_threshold_list_name: turnThresholdListName.trim() || null,
    notification_format: notifFormat,
    notify_ppv_sent: optOuts.ppvSent,
    notify_free_content_sent: optOuts.freeContentSent,
    notify_tip_ask: optOuts.tipAsk,
    notify_vip_offer_sent: optOuts.vipOfferSent,
    notify_chat_ended: optOuts.chatEnded,
    notify_all_payment_events: optOuts.allPaymentEvents,
  }), [
    interruptListName, ignoreDetectionTime, notificationLanguage, trackingMode, trackingCustomName, account.chat_tracking_custom_name,
    trackingCleanupEnabled, trackingCleanupDays, turnThreshold, turnThresholdNotify, turnThresholdListName, notifFormat, optOuts,
  ]);
  const accountAuto = useAutosave({
    value: accountDraft,
    validate: () =>
      interruptListNameError ||
      (trackingCustomNameError === 'empty'
        ? t('notifications.chatTracking.errEmpty')
        : trackingCustomNameError === 'ddmm'
          ? t('notifications.chatTracking.errDdmm')
          : trackingCustomNameError
            ? t('notifications.chatTracking.errReserved')
            : null),
    save: async (draft) => {
      // Surface failures instead of swallowing them — a silent save once masked a
      // missing column pre-migration and left the account unsaved while looking fine.
      const updated = await api.updateAccount(account.account_id, draft);
      onAccountUpdate?.(updated);
    },
  });

  const spamAuto = useAutosave({
    value: { enabled: spamEnabled, spam_list_name: spamFolder.trim() },
    enabled: spamLoaded,
    save: async (v) => { await api.putSpamPolicy(account.account_id, v); },
  });

  // One chip for all of it: the worst state wins.
  const saveChip = (() => {
    const all = [
      { status: accountAuto.status, error: accountAuto.error, retry: accountAuto.retry },
      { status: spamAuto.status, error: spamAuto.error, retry: spamAuto.retry },
      { status: quick.status, error: quick.error, retry: undefined as (() => void) | undefined },
    ];
    for (const st of ['invalid', 'error', 'saving', 'saved'] as AutosaveStatus[]) {
      const hit = all.find((x) => x.status === st);
      if (hit) return hit;
    }
    return null;
  })();

  const groups = useMemo(() => groupInterrupts(notifications), [notifications]);
  const { enabled: enabledCount, total: totalCount } = useMemo(
    () => interruptsEnabledSummary(groups),
    [groups],
  );

  const longChatOn = isLongChatFlagOn(turnThreshold);
  const moneyInOnlyAvailable = canApplyMoneyInOnly(optOuts);

  // ── Trigger row shared by both columns ───────────────────────────────
  const renderTrigger = (n: InterruptStatus) => {
    const iKey = interruptNameToKey(n.name);
    const label = t(`interrupts.${iKey}` as Parameters<typeof t>[0], { defaultValue: formatInterruptText(n.name) });
    const desc = t(`interrupts.${iKey}Desc` as Parameters<typeof t>[0], { defaultValue: formatInterruptText(n.description) });
    // "Fan stopped responding" carries its detection window inline, per the design.
    const withParam = isFanStoppedRespondingInterrupt(n);
    return (
      <div
        key={n.id}
        title={desc}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          minHeight: 32, borderTop: `1px solid ${C.rowLine}`,
        }}
      >
        <span style={{ font: `400 12.5px/1.3 ${FONT}`, color: n.status ? C.text : C.muted }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          {withParam && n.status && (
            <Stepper
              compact
              label={t('notifications.minutesValue', { value: detectionMinutesLabel(ignoreDetectionTime) })}
              onDec={() => { setIgnoreDetectionTime((v) => stepDetectionSeconds(v, -1)); }}
              onInc={() => { setIgnoreDetectionTime((v) => stepDetectionSeconds(v, +1)); }}
            />
          )}
          <MiniToggle on={n.status} onChange={() => toggleNotification(n.id)} />
        </div>
      </div>
    );
  };

  const renderColumn = (items: InterruptStatus[], label: string, color: string, borderRight: boolean) => (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '8px 14px 10px', borderRight: borderRight ? `1px solid ${C.rowLine}` : undefined, minWidth: 0 }}>
      <span style={{ font: `600 10.5px ${FONT}`, letterSpacing: '.06em', color, padding: '4px 0' }}>{label}</span>
      {items.map(renderTrigger)}
    </div>
  );

  // ── Informational (notification-only) rows ───────────────────────────
  const infoRows: { key: keyof ActivityOptOuts; label: string; desc: string }[] = [
    { key: 'ppvSent', label: t('notifications.optPpvSent'), desc: t('notifications.optPpvSentDesc') },
    { key: 'vipOfferSent', label: t('notifications.optVipOfferSent'), desc: t('notifications.optVipOfferSentDesc') },
    { key: 'freeContentSent', label: t('notifications.optFreeContentSent'), desc: t('notifications.optFreeContentSentDesc') },
    { key: 'tipAsk', label: t('notifications.optTipAsk'), desc: t('notifications.optTipAskDesc') },
    { key: 'chatEnded', label: t('notifications.optChatEnded'), desc: t('notifications.optChatEndedDesc') },
    { key: 'allPaymentEvents', label: t('notifications.optAllPaymentEvents'), desc: t('notifications.optAllPaymentEventsDesc') },
  ];

  const telegramStatusLine = (() => {
    if (telegramSaveState === 'saving') return { tone: 'muted' as const, text: t('notifications.telegramSaving') };
    if (telegramSaveState === 'error') return { tone: 'error' as const, text: t('notifications.telegramSaveFailed') };
    if (telegramSaveState === 'saved') return { tone: 'ok' as const, text: t('notifications.telegramSaved') };
    return null;
  })();

  return (
    <>
      <div style={{ background: C.bg, color: C.text, border: `1px solid ${C.frameLine}`, borderRadius: 10, fontFamily: FONT, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%' }}>
          <div style={{ padding: '24px 28px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Two equal halves: General on the left, the trigger + ping lists on the
                right. Collapses to one column below 1100px (see .notifPageCols). */}
            <div className="notifPageCols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 18, alignItems: 'start' }}>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
                {/* ── GENERAL ─────────────────────────────────────────────── */}
                <Section label={t('notifications.sections.general')}>
                  <Row highlight first>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ font: `600 13px/1.3 ${FONT}`, color: C.text }}>{t('notifications.telegramChatId')}</span>
                        <WarnTip
                          title={t('notifications.telegramTipTitle')}
                          body={
                            <Trans
                              t={t}
                              i18nKey="notifications.telegramInfo"
                              components={{ 1: <br />, 2: <br />, 3: <br />, 4: <br />, b: <b /> }}
                            />
                          }
                        />
                        <span style={{ font: `600 9.5px ${FONT}`, letterSpacing: '.06em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 4, background: C.accent, color: '#fff' }}>
                          {t('notifications.required')}
                        </span>
                      </div>
                      <span style={{ font: `400 11.5px/1.35 ${FONT}`, color: C.accentSoft }}>{t('notifications.telegramRequiredHint')}</span>
                      {telegramStatusLine && (
                        <span style={{ font: `500 11.5px/1.35 ${FONT}`, color: telegramStatusLine.tone === 'ok' ? C.ok : telegramStatusLine.tone === 'error' ? C.err : C.dim }}>
                          {telegramSaveState === 'saving' ? <Spinner size={12} label={telegramStatusLine.text} /> : telegramStatusLine.text}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <input
                        value={telegramChatId}
                        onChange={(e) => setTelegramChatId(e.target.value)}
                        placeholder={t('notifications.telegramPlaceholder')}
                        spellCheck={false}
                        style={{ ...fieldStyle(210, 32), border: `1px solid ${C.accent}`, boxShadow: C.accentRing, fontWeight: 500, fontSize: 13 }}
                      />
                      <button
                        type="button"
                        onClick={handleTestTelegram}
                        disabled={testingTelegram}
                        style={{
                          height: 32, padding: '0 14px', background: C.accent, border: 0, borderRadius: 6,
                          color: '#fff', font: `600 12px ${FONT}`, cursor: testingTelegram ? 'not-allowed' : 'pointer',
                          opacity: testingTelegram ? 0.7 : 1,
                        }}
                      >
                        <BusyLabel busy={testingTelegram} label={t('notifications.sending')}>{t('notifications.test')}</BusyLabel>
                      </button>
                    </div>
                  </Row>

                  {/* Test result / delivery-health advisory */}
                  {(testStatus !== 'idle' || (account.telegram_health && account.telegram_health !== 'unknown')) && (
                    <SubRow>
                      {testStatus === 'sent' && (
                        <Chip tone="ok">
                          {testMigrated ? t('notifications.testSentMigrated') : t('notifications.testSent')}
                        </Chip>
                      )}
                      {testStatus === 'error' && <Chip tone="error">{testErrorMsg || t('notifications.testFailed')}</Chip>}
                      {testStatus === 'idle' && (() => {
                        const h = account.telegram_health;
                        const detail = account.telegram_health_detail;
                        if (h === 'ok') return <Chip tone="ok">{t('notifications.healthOk')}</Chip>;
                        if (h === 'migrated') return <Chip tone="ok">{detail || t('notifications.healthMigrated')}</Chip>;
                        // bot_not_in_group | unconfigured — the operator must act.
                        return <Chip tone="warn">{detail || t('notifications.healthBotMissing')}</Chip>;
                      })()}
                    </SubRow>
                  )}

                  <Row title={t('notifications.language')}>
                    <Seg
                      value={notificationLanguage}
                      options={LANG_OPTIONS}
                      onChange={(lang) => { setNotificationLanguage(lang); }}
                    />
                  </Row>

                  <Row title={t('notifications.format')} desc={t('notifications.formatCompactHint')}>
                    <Seg<NotifFormat>
                      value={notifFormat}
                      options={[
                        { id: 'verbose', label: t('notifications.formatVerbose') },
                        { id: 'compact', label: t('notifications.formatCompact') },
                      ]}
                      onChange={(f) => { setNotifFormat(f); }}
                    />
                  </Row>

                  <Row title={t('notifications.chatTracking.rowTitle')} desc={t('notifications.chatTracking.rowDesc')}>
                    <Seg<TrackingMode>
                      value={trackingMode}
                      options={[
                        { id: 'off', label: t('notifications.chatTracking.modeOff') },
                        { id: 'daily', label: t('notifications.chatTracking.modeDaily') },
                        { id: 'custom', label: t('notifications.chatTracking.modeCustom') },
                      ]}
                      onChange={(m) => { setTrackingMode(m); }}
                    />
                  </Row>

                  {trackingMode === 'custom' && (
                    <SubRow label={t('notifications.chatTracking.customNameLabel')}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        <input
                          value={trackingCustomName}
                          onChange={(e) => {
                            setTrackingCustomName(e.target.value);
                           
                          }}
                          placeholder={t('notifications.chatTracking.customNamePlaceholder')}
                          style={fieldStyle(190, 26)}
                        />
                        {trackingCustomNameError && (
                          <Chip tone="error">
                            {trackingCustomNameError === 'empty'
                              ? t('notifications.chatTracking.errEmpty')
                              : trackingCustomNameError === 'ddmm'
                                ? t('notifications.chatTracking.errDdmm')
                                : t('notifications.chatTracking.errReserved')}
                          </Chip>
                        )}
                      </div>
                    </SubRow>
                  )}

                  {trackingMode !== 'off' && (
                    <SubRow
                      label={trackingMode === 'daily'
                        ? t('notifications.chatTracking.dailyRowLabel')
                        : t('notifications.chatTracking.customCleanupRowLabel')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ font: `400 12px ${FONT}`, color: C.dim }}>{t('notifications.chatTracking.afterWord')}</span>
                        <Stepper
                          label={t('notifications.chatTracking.daysShort', { count: trackingCleanupDays })}
                          disabled={!trackingCleanupEnabled}
                          onDec={() => { setTrackingCleanupDays((v) => stepCleanupDays(v, -1)); }}
                          onInc={() => { setTrackingCleanupDays((v) => stepCleanupDays(v, +1)); }}
                        />
                        <MiniToggle
                          on={trackingCleanupEnabled}
                          onChange={() => { setTrackingCleanupEnabled((v) => !v); }}
                        />
                      </div>
                    </SubRow>
                  )}

                  <Row title={t('notifications.spam.title')} desc={t('notifications.spam.rowDesc')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        value={spamFolder}
                        disabled={!spamEnabled}
                        onChange={(e) => { setSpamFolder(e.target.value); }}
                        placeholder="ignored_spam"
                        spellCheck={false}
                        title={t('notifications.spam.folderHint')}
                        style={{ ...fieldStyle(150, 28), opacity: spamEnabled ? 1 : 0.45 }}
                      />
                      <MiniToggle on={spamEnabled} onChange={() => { setSpamEnabled((v) => !v); }} />
                    </div>
                  </Row>

                  <Row title={t('notifications.fanSentContent.title')} desc={t('notifications.fanSentContent.rowDesc')}>
                    <Seg<FanSentMode>
                      value={fanSentMode}
                      options={[
                        { id: 'analyse', label: t('notifications.fanSentContent.modeAnalyse'), badge: t('notifications.fanSentContent.recommendedShort') },
                        { id: 'handoff', label: t('notifications.fanSentContent.modeHandoff') },
                        { id: 'ignore', label: t('notifications.fanSentContent.modeIgnore') },
                      ]}
                      onChange={changeFanSentMode}
                    />
                  </Row>

                  <Row title={t('notifications.turnThreshold.rowTitle')} desc={t('notifications.turnThreshold.rowDesc')}>
                    <Stepper
                      label={longChatOn ? String(turnThreshold) : t('notifications.turnThreshold.off')}
                      wide
                      onDec={() => { setTurnThreshold((v) => stepTurnThreshold(v, -1)); }}
                      onInc={() => { setTurnThreshold((v) => stepTurnThreshold(v, +1)); }}
                    />
                  </Row>

                  {longChatOn && (
                    <SubRow
                      label={t('notifications.turnThreshold.subRowLabel')}
                      labelTitle={t('notifications.turnThreshold.subRowHint')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input
                          value={turnThresholdListName}
                          onChange={(e) => { setTurnThresholdListName(e.target.value); }}
                          placeholder={t('notifications.turnThreshold.listPlaceholder')}
                          style={fieldStyle(150, 26)}
                        />
                        <MiniToggle
                          on={turnThresholdNotify}
                          onChange={() => { setTurnThresholdNotify((v) => !v); }}
                        />
                      </div>
                    </SubRow>
                  )}
                </Section>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
                {/* ── INTERRUPTS ──────────────────────────────────────────── */}
                <Section label={t('notifications.sections.interrupts')}>
                  <Row highlight first divider>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      <span style={{ font: `500 13px/1.3 ${FONT}`, color: C.text }}>{t('notifications.interruptsHeaderTitle')}</span>
                      <span style={{ font: `400 11.5px/1.35 ${FONT}`, color: C.muted }}>
                        {t('notifications.interruptsHeaderDesc', { enabled: enabledCount, total: totalCount })}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ font: `600 12px ${FONT}`, color: C.text }}>{t('notifications.folderLabel')}</span>
                      <WarnTip
                        align="right"
                        title={t('notifications.folderTipTitle')}
                        body={t('notifications.folderTipBody')}
                        note={t('notifications.folderTipWarn')}
                        noteTone="warn"
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        <input
                          value={interruptListName}
                          onChange={(e) => {
                            setInterruptListName(e.target.value);
                           
                          }}
                          placeholder="Notifications"
                          spellCheck={false}
                          style={{ ...fieldStyle(170, 32), border: `1px solid ${C.accent}`, boxShadow: C.accentRing, fontWeight: 500, fontSize: 13 }}
                        />
                        {interruptListNameError && <Chip tone="error">{interruptListNameError}</Chip>}
                      </div>
                    </div>
                  </Row>

                  {loading ? (
                    <div style={{ padding: '10px 14px' }}>
                      <LoadingBlock pad="8px 2px" size={18} label={t('common:loading')} />
                    </div>
                  ) : totalCount === 0 ? (
                    <div style={{ padding: '12px 14px', font: `400 12.5px ${FONT}`, color: C.muted }}>
                      {t('notifications.noneConfigured')}
                    </div>
                  ) : (
                    <>
                      <div className="notifCols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
                        {renderColumn(groups.sales, t('notifications.columns.sales'), C.accentLite, true)}
                        {renderColumn(groups.risk, t('notifications.columns.risk'), C.warn, false)}
                      </div>
                      {groups.other.length > 0 && (
                        <div style={{ borderTop: `1px solid ${C.rowLine}` }}>
                          {renderColumn(groups.other, t('notifications.columns.other'), C.muted, false)}
                        </div>
                      )}
                    </>
                  )}
                </Section>

                {/* ── INFORMATIONAL ───────────────────────────────────────── */}
                <Section label={t('notifications.sections.informational')}>
                  <Row first divider>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      <span style={{ font: `500 13px/1.3 ${FONT}`, color: C.text }}>{t('notifications.infoHeaderTitle')}</span>
                      <span style={{ font: `400 11.5px/1.35 ${FONT}`, color: C.muted }}>{t('notifications.infoHeaderDesc')}</span>
                    </div>
                    <button
                      type="button"
                      disabled={!moneyInOnlyAvailable}
                      onClick={() => { setOptOuts((p) => moneyInOnly(p)); }}
                      style={{
                        height: 28, padding: '0 12px', background: 'transparent', border: `1px solid ${C.cardLine}`,
                        borderRadius: 6, color: C.text, font: `500 12px ${FONT}`, whiteSpace: 'nowrap',
                        cursor: !moneyInOnlyAvailable ? 'not-allowed' : 'pointer',
                        opacity: !moneyInOnlyAvailable ? 0.45 : 1,
                      }}
                    >
                      {t('notifications.moneyInOnly')}
                    </button>
                  </Row>
                  <div className="notifCols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', columnGap: 28, padding: '4px 14px 8px' }}>
                    {infoRows.map(({ key, label, desc }) => (
                      <div
                        key={key}
                        title={desc}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minHeight: 32, borderBottom: `1px solid ${C.rowLine}` }}
                      >
                        <span style={{ font: `400 12.5px/1.3 ${FONT}`, color: optOuts[key] ? C.text : C.muted }}>{label}</span>
                        <MiniToggle
                          on={optOuts[key]}
                          onChange={() => { setOptOuts((p) => ({ ...p, [key]: !p[key] })); }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, font: `400 11.5px ${FONT}`, color: C.dim }}>
                      <FeatherIcon name="lock" size={12} /> {t('notifications.moneyInNote')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, font: `400 11.5px ${FONT}`, color: C.dim }}>
                      <FeatherIcon name="arrow-up" size={12} /> {t('notifications.fanRequestsMovedNote')}
                    </div>
                  </div>
                </Section>
              </div>

            </div>

          </div>
        </div>
      </div>

      {/* Save-status chip — every field saves on change, so this only reports
          progress / problems. Portaled so AccountView's PageTransition transform can't anchor it. */}
      {createPortal(
        <div
          className="mobile-bottom-bar"
          style={{
            position: 'fixed', right: 24, bottom: 20, zIndex: 1000, fontFamily: FONT,
            transform: saveChip ? 'translateY(0)' : 'translateY(160%)', opacity: saveChip ? 1 : 0,
            transition: 'transform .25s cubic-bezier(.3,1,.4,1), opacity .2s', pointerEvents: saveChip ? 'auto' : 'none',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 999, maxWidth: 380,
              background: C.card, border: `1px solid ${C.cardLine}`, boxShadow: '0 16px 44px -12px rgba(0,0,0,.7)',
              font: `500 12.5px ${FONT}`,
              color: saveChip?.status === 'error' || saveChip?.status === 'invalid' ? C.accentSoft : saveChip?.status === 'saved' ? C.accentSoft : C.muted,
            }}
          >
            {saveChip?.status === 'saving' ? <Spinner size={13} label={t('notifications.autosaveSaving')} /> : <FeatherIcon name={saveChip?.status === 'saved' ? 'check' : 'alert-circle'} size={13} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {saveChip?.status === 'saving'
                ? null
                : saveChip?.status === 'saved'
                  ? t('notifications.autosaveSaved')
                  : saveChip?.status === 'invalid'
                    ? saveChip.error
                    : saveChip?.error || t('notifications.failedSave')}
            </span>
            {saveChip?.status === 'error' && saveChip.retry && (
              <button type="button" onClick={saveChip.retry} style={{ border: 0, background: 'transparent', color: C.accentLite, font: `600 12.5px ${FONT}`, cursor: 'pointer', padding: 0 }}>
                {t('notifications.autosaveRetry')}
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// ── Building blocks ──────────────────────────────────────────────────

/** Uppercase section label + the card its rows live in. */
function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ font: `600 10.5px ${FONT}`, letterSpacing: '.08em', textTransform: 'uppercase', color: C.dim }}>{label}</span>
      <div style={{ background: C.card, border: `1px solid ${C.cardLine}`, borderRadius: 10 }}>{children}</div>
    </div>
  );
}

/** A settings row: label (+ optional description) on the left, control on the right.
 *  `highlight` paints the accent-tinted "this one matters" treatment from the design. */
function Row({ title, desc, highlight, first, divider, children }: {
  title?: string;
  desc?: string;
  highlight?: boolean;
  first?: boolean;
  /** Draw the separator below this row — for a header row followed by a grid
   *  rather than by another Row (which would bring its own top border). */
  divider?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
        padding: highlight ? '12px 14px' : '10px 14px',
        borderTop: first ? undefined : `1px solid ${C.rowLine}`,
        borderBottom: divider ? `1px solid ${C.rowLine}` : undefined,
        background: highlight ? C.accentTint : undefined,
        boxShadow: highlight ? `inset 3px 0 0 ${C.accent}` : undefined,
        borderRadius: first ? '9px 9px 0 0' : undefined,
      }}
    >
      {title != null ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ font: `500 13px/1.3 ${FONT}`, color: C.text }}>{title}</span>
          {desc && <span style={{ font: `400 11.5px/1.35 ${FONT}`, color: C.muted }}>{desc}</span>}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** An indented continuation of the row above it. */
function SubRow({ label, labelTitle, children }: { label?: string; labelTitle?: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
        padding: '8px 14px 8px 30px', background: C.subRow, borderTop: `1px solid ${C.rowLine}`,
      }}
    >
      {label != null && (
        <span title={labelTitle} style={{ font: `400 12.5px ${FONT}`, color: C.muted }}>{label}</span>
      )}
      {children}
    </div>
  );
}

/** Compact segmented control. */
function Seg<T extends string>({ value, options, disabled, onChange }: {
  value: T;
  options: { id: T; label: string; badge?: string }[];
  disabled?: boolean;
  onChange: (id: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      style={{
        display: 'flex', gap: 2, padding: 2, background: C.input, border: `1px solid ${C.cardLine}`,
        borderRadius: 7, opacity: disabled ? 0.6 : 1, maxWidth: '100%', flexWrap: 'wrap',
      }}
    >
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(o.id)}
            style={{
              height: 24, padding: '0 11px', border: 0, borderRadius: 5, font: `500 12px ${FONT}`,
              background: active ? C.accent : 'transparent', color: active ? '#fff' : C.muted,
              cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', flex: 'none',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {o.label}
            {o.badge && (
              <span style={{
                font: `600 9.5px ${FONT}`, padding: '1px 4px', borderRadius: 3,
                background: active ? 'color-mix(in srgb, var(--om-ink) 22%, transparent)' : 'color-mix(in srgb, var(--om-accent) 25%, transparent)',
                color: active ? '#fff' : C.accentSoft,
              }}>
                {o.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** 28×16 pill switch. */
function MiniToggle({ on, disabled, onChange }: { on: boolean; disabled?: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onChange}
      style={{
        width: 28, height: 16, borderRadius: 99, border: 0, padding: 2, flex: 'none',
        background: on ? C.accent : 'var(--om-border-hi)', cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', transition: 'background .15s', opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{
        width: 12, height: 12, borderRadius: 99, background: '#fff',
        transform: on ? 'translateX(12px)' : 'translateX(0)', transition: 'transform .15s',
      }} />
    </button>
  );
}

/** −/+ number stepper. `compact` is the inline variant used inside a trigger row. */
function Stepper({ label, onDec, onInc, disabled, compact, wide }: {
  label: string;
  onDec: () => void;
  onInc: () => void;
  disabled?: boolean;
  compact?: boolean;
  wide?: boolean;
}) {
  const h = compact ? 22 : 26;
  const btn = compact ? 20 : 24;
  const btnStyle: CSSProperties = {
    width: btn, height: btn, border: 0, background: 'transparent', color: C.muted,
    cursor: disabled ? 'not-allowed' : 'pointer', font: `500 ${compact ? 12 : 14}px ${FONT}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', height: h, boxSizing: 'border-box',
      border: `1px solid ${C.cardLine}`, borderRadius: compact ? 5 : 6, background: C.input,
      opacity: disabled ? 0.5 : 1, flex: 'none',
    }}>
      <button type="button" disabled={disabled} onClick={onDec} style={btnStyle} aria-label="−">−</button>
      <span style={{
        minWidth: wide ? 64 : compact ? 44 : 52, textAlign: 'center',
        font: `600 ${compact ? 11.5 : 12}px ${FONT}`, color: C.text, fontVariantNumeric: 'tabular-nums',
      }}>
        {label}
      </span>
      <button type="button" disabled={disabled} onClick={onInc} style={btnStyle} aria-label="+">+</button>
    </div>
  );
}

/** Inline status chip (test result, validation error, delivery health). */
function Chip({ tone, children }: { tone: 'ok' | 'warn' | 'error'; children: ReactNode }) {
  const col = tone === 'ok' ? C.ok : tone === 'warn' ? '#f5a524' : C.err;
  const bg = tone === 'ok' ? 'rgba(61,220,132,.14)' : tone === 'warn' ? 'rgba(245,165,36,.14)' : 'rgba(248,113,113,.14)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, font: `500 11.5px/1.4 ${FONT}`,
      color: col, background: bg, padding: '5px 10px', borderRadius: 6,
    }}>
      {children}
    </span>
  );
}

/** The orange "!" help marker and its popover. Portaled to <body> with fixed
 *  positioning so it escapes the card's rounded/overflow context. */
function WarnTip({ title, body, note, noteTone, align = 'left' }: {
  title: string;
  body: ReactNode;
  note?: string;
  noteTone?: 'warn' | 'muted';
  align?: 'left' | 'right';
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const open = () => { if (ref.current) setRect(ref.current.getBoundingClientRect()); };
  const close = () => setRect(null);

  // React's synthetic onMouseLeave can be swallowed by portal routing, so watch
  // the pointer at the document level and close once it leaves the marker.
  useEffect(() => {
    if (!rect) return;
    const onMove = (e: PointerEvent) => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const pad = 4;
      const inside = e.clientX >= r.left - pad && e.clientX <= r.right + pad
        && e.clientY >= r.top - pad && e.clientY <= r.bottom + pad;
      if (!inside) close();
    };
    document.addEventListener('pointermove', onMove, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('blur', close);
    return () => {
      document.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('blur', close);
    };
  }, [rect]);

  const W = 300;
  const left = rect
    ? align === 'right'
      ? Math.max(12, Math.min(rect.right + 10 - W, window.innerWidth - W - 12))
      : Math.max(12, Math.min(rect.left - 10, window.innerWidth - W - 12))
    : 0;

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={open}
        onMouseLeave={close}
        style={{
          width: 18, height: 18, borderRadius: 99, background: C.warn, color: C.bg,
          font: `800 12px/18px ${FONT}`, textAlign: 'center', cursor: 'help', flex: 'none',
        }}
      >
        !
      </span>
      {rect && createPortal(
        <div style={{
          position: 'fixed', top: rect.bottom + 8, left, zIndex: 3000, width: W, boxSizing: 'border-box',
          padding: '12px 14px', background: C.pop, border: `1px solid ${C.popLine}`, borderRadius: 8,
          boxShadow: '0 10px 30px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 6,
          fontFamily: FONT, textAlign: 'left', pointerEvents: 'none',
        }}>
          <span style={{ font: `600 12.5px ${FONT}`, color: C.text }}>{title}</span>
          <span style={{ font: `400 12px/1.5 ${FONT}`, color: 'var(--om-body)', textWrap: 'pretty' }}>{body}</span>
          {note && (
            <span style={{ font: `${noteTone === 'warn' ? 600 : 400} 11.5px/1.45 ${FONT}`, color: noteTone === 'warn' ? C.warn : 'var(--om-sub)', textWrap: 'pretty' }}>
              {note}
            </span>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

/** Shared compact input styling. */
function fieldStyle(width: number, height: number): CSSProperties {
  return {
    width, height, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.cardLine}`,
    borderRadius: 6, padding: '0 10px', color: C.text, font: `400 12.5px ${FONT}`, outline: 'none',
    maxWidth: '100%',
  };
}
